import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import SchemaOrg from '@/components/base/SchemaOrg';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();
  const [activeCategory, setActiveCategory] = useState(slug || categories[0].slug);

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
  }, [slug]);

  useEffect(() => {
    document.title = `${categories.find(c => c.slug === activeCategory)?.name || ''} - g点网`;
  }, [activeCategory]);

  const currentCat = categories.find((c) => c.slug === activeCategory);
  const articles = getArticlesByCategory(activeCategory);

  const breadcrumbItems = [
    { label: t('category'), path: '/category/platform-news' },
    { label: currentCat?.name || '' },
  ];

  const breadcrumbSchema = {
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: t('breadcrumbHome'), item: 'https://gpoint.com/' },
      { '@type': 'ListItem', position: 2, name: t('category'), item: 'https://gpoint.com/category/platform-news' },
      { '@type': 'ListItem', position: 3, name: currentCat?.name || '' },
    ],
  };

  return (
    <>
      <SchemaOrg type="BreadcrumbList" data={breadcrumbSchema} />
      <SchemaOrg type="WebPage" data={{
        name: `${currentCat?.name || ''} - g点网`,
        url: `https://gpoint.com/category/${activeCategory}`,
        description: currentCat?.description || '',
        inLanguage: 'zh-CN',
      }} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="w-full px-4 md:px-6 py-6">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />
            </div>
          </div>

          <section className="w-full px-4 md:px-6 py-6">
            <div className="max-w-7xl mx-auto">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
                {currentCat?.name}
              </h1>
              <p className="text-sm text-foreground-500 mb-2">{currentCat?.description}</p>
              <p className="text-xs text-foreground-400">{articles.length} {t('articleCount')}</p>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 pb-4">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-2 flex-wrap">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 text-sm font-medium rounded-full transition-all whitespace-nowrap ${
                      activeCategory === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-50 border border-secondary-200 text-foreground-600 hover:text-foreground-900 hover:border-secondary-300'
                    }`}
                  >
                    <i className={`${cat.icon} mr-1.5`}></i>
                    {cat.name}
                    <span className="ml-1 text-xs opacity-70">({cat.articleCount})</span>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          <section className="w-full px-4 md:px-6 py-8">
            <div className="max-w-7xl mx-auto">
              {articles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {articles.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100 text-foreground-400">
                    <i className="ri-inbox-line text-2xl"></i>
                  </div>
                  <p className="text-foreground-500">{t('noArticles')}</p>
                </div>
              )}
            </div>
          </section>
        </main>
        <Footer />
        <ScrollToTop />
      </div>
    </>
  );
}